import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Toast } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getTodayLocalDateString, getDateWithOffset } from '../utils/dateUtils';
import { useAddRecipeDislike, useDeleteRecipeDislike, useGetRecipeDislikeStatus } from '../hooks/dislikeRecipe';
import { useAddRecipeFavorite, useDeleteRecipeFavorite, useGetRecipeFavoriteStatus } from '../hooks/favoriteRecipe';
import { getIconUrl } from '../utils/imageUtils';

const CompleteMealCookingButton = ({ mealId, appUserId, servingSize, mealName, recipeId, onComplete }) => {
  const { sessionToken } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [actualServings, setActualServings] = useState(servingSize);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [errorCompletingCooking, setErrorCompletingCooking] = useState(false);
  const [errorCompletingCookingMessage, setErrorCompletingCookingMessage] = useState('');
  
  // Dislike hooks and state
  const { addRecipeDislike } = useAddRecipeDislike();
  const { deleteRecipeDislike } = useDeleteRecipeDislike();
  const { getRecipeDislikeStatus } = useGetRecipeDislikeStatus();
  const [isDisliked, setIsDisliked] = useState(false);
  const [isTogglingDislike, setIsTogglingDislike] = useState(false);

  // Favorite hooks and state
  const { addRecipeFavorite } = useAddRecipeFavorite();
  const { deleteRecipeFavorite } = useDeleteRecipeFavorite();
  const { getRecipeFavoriteStatus } = useGetRecipeFavoriteStatus();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  useEffect(() => {
    setActualServings(servingSize);
  }, [servingSize]);

  // Check if recipe is disliked when component mounts or recipeId changes
  useEffect(() => {
    const checkDislikeStatus = async () => {
      if (recipeId && sessionToken) {
        try {
          const status = await getRecipeDislikeStatus(recipeId);
          setIsDisliked(status);
        } catch (error) {
          console.error('Error checking dislike status:', error);
        }
      }
    };
    checkDislikeStatus();
  }, [recipeId, sessionToken]);

  // Check if recipe is favorited when component mounts or recipeId changes
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (recipeId && sessionToken) {
        try {
          const status = await getRecipeFavoriteStatus(recipeId);
          setIsFavorited(status);
        } catch (error) {
          console.error('Error checking favorite status:', error);
        }
      }
    };
    checkFavoriteStatus();
  }, [recipeId, sessionToken]);

  // Handle toggling dislike status
  const handleToggleDislike = async (e) => {
    e.stopPropagation();
    if (!recipeId || isTogglingDislike) return;
    
    setIsTogglingDislike(true);
    try {
      if (isDisliked) {
        const success = await deleteRecipeDislike(recipeId);
        if (success) {
          setIsDisliked(false);
        }
      } else {
        const success = await addRecipeDislike(recipeId);
        if (success) {
          setIsDisliked(true);
        }
      }
    } catch (error) {
      console.error('Error toggling dislike:', error);
    } finally {
      setIsTogglingDislike(false);
    }
  };

  // Handle toggling favorite status
  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    if (!recipeId || isTogglingFavorite) return;
    
    setIsTogglingFavorite(true);
    try {
      if (isFavorited) {
        const success = await deleteRecipeFavorite(recipeId);
        if (success) {
          setIsFavorited(false);
        }
      } else {
        const success = await addRecipeFavorite(recipeId);
        if (success) {
          setIsFavorited(true);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const showMealCompletedToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
        setShowToast(false);
    }, 5000);
  };

  const handleCompleteCooking = async () => {

    const todayBrowserDate = getTodayLocalDateString();
    const nextWeekBrowserDate = getDateWithOffset(7);

    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }

      const response = await fetchWithCSRF('/api/v1/meal/complete_meal_cooking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          meal_id: mealId,
          serving_size: parseInt(actualServings),
          today_browser_date: todayBrowserDate,
          next_week_browser_date: nextWeekBrowserDate
        })
      });

      //

      if (!response.ok) {
        throw new Error('Failed to complete cooking for this meal.');
      }

      const data = await response.json();

      sessionStorage.removeItem('currentMealId');
      setShowModal(false);
      setErrorCompletingCooking(false);
      setErrorCompletingCookingMessage('');
      showMealCompletedToast('Ingredients removed from pantry!');
      
      // Call onComplete callback to navigate back to meal plan
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error completing meal cooking:', error);
      setErrorCompletingCooking(true);
      setErrorCompletingCookingMessage('Failed to mark meal as cooked. Please refresh the page and try again.');
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handleDecreaseServings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActualServings(prev => Math.max(1, prev - 1));
  };

  const handleIncreaseServings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActualServings(prev => Math.min(16, prev + 1));
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('currentMealId');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        className="add-to-meal-plan-btn"
        style={{ width: '100%', marginBottom: '2rem', paddingTop: '15px !important', paddingBottom: '15px !important', fontSize: '1.25rem' }}
      >
        I Cooked This
      </button>

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        backdrop="static"
        keyboard={false}
        onClick={handleModalClick}
      >
        <Modal.Header closeButton>
          <Modal.Title>Complete Cooking for {mealName}</Modal.Title>
        </Modal.Header>
        <Modal.Body onClick={handleModalClick}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="text-center w-100">How many servings did you cook?</Form.Label>
              <div className="d-flex align-items-center justify-content-center mt-4 gap-3" onClick={handleModalClick}>
                <button 
                  type="button"
                  className="btn p-0" 
                  onClick={handleDecreaseServings}
                  style={{ 
                    border: 'none', 
                    background: 'none',
                    color: '#52cd92',
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    fontWeight: 'bold'
                  }}
                >
                  −
                </button>
                <div className="d-flex flex-column align-items-center">
                  <span className="h4 mb-0">{actualServings}</span>
                  <small className="text-muted">{actualServings === 1 ? 'serving' : 'servings'}</small>
                </div>
                <button 
                  type="button"
                  className="btn p-0" 
                  onClick={handleIncreaseServings}
                  style={{ 
                    border: 'none', 
                    background: 'none',
                    color: '#52cd92',
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    fontWeight: 'bold'
                  }}
                >
                  +
                </button>
              </div>
              <small className="text-muted mt-1 mb-4 d-block text-center">
                Completing this meal will remove the ingredients used in this meal from your pantry
              </small>
            </Form.Group>

            {/* Favorite and Dislike Buttons */}
            <div className="d-flex justify-content-center gap-3 mt-4">
              {/* Favorite Button */}
              <button
                type="button"
                className="btn d-flex align-items-center gap-2"
                style={{
                  padding: '7px 16px',
                  fontSize: '1rem',
                  borderRadius: '4px',
                  backgroundColor: isFavorited ? '#e0146a' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  transition: 'background-color 0.2s'
                }}
                onClick={handleToggleFavorite}
                disabled={isTogglingFavorite}
                title={isFavorited ? "Remove from favorites" : "Favorite this recipe"}
              >
                <img 
                  src={getIconUrl(isFavorited ? 'filled-heart.svg' : 'heart-outline.svg')} 
                  alt="Favorite" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    filter: 'brightness(0) invert(1)' // Make icon white to match button text
                  }}
                />
                {isFavorited ? 'Favorited' : 'Add to Favorites'}
              </button>

              {/* Dislike Button */}
              <button
                type="button"
                className="btn d-flex align-items-center gap-2"
                style={{
                  padding: '7px 16px',
                  fontSize: '1rem',
                  borderRadius: '4px',
                  backgroundColor: isDisliked ? '#dc3545' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  transition: 'background-color 0.2s'
                }}
                onClick={handleToggleDislike}
                disabled={isTogglingDislike}
                title={isDisliked ? "Remove from dislikes" : "Dislike this recipe"}
              >
                <img 
                  src={getIconUrl(isDisliked ? 'thumbDown-filled.svg' : 'thumbDown.svg')} 
                  alt="Dislike" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    filter: 'brightness(0) invert(1)' // Make icon white to match button text
                  }}
                />
                {isDisliked ? 'Disliked' : 'Dislike'}
              </button>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer onClick={handleModalClick}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCompleteCooking} className="add-to-meal-plan-btn">
            Complete Cooking
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Completing Cooking Toast Notification */}
      <Toast 
          show={errorCompletingCooking} 
          onClose={() => setErrorCompletingCooking(false)}
          style={{
              position: 'fixed',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              backgroundColor: '#dc3545',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              width: '50%'
          }}
      >
          <Toast.Header style={{ 
              backgroundColor: '#dc3545', 
              borderBottom: '1px solid rgba(255,255,255,0.2)',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              width: '100%'
          }}>
              <strong style={{ color: 'white' }}>{errorCompletingCookingMessage}</strong>
          </Toast.Header>
      </Toast>

      {/* Toast Notification */}
      <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)}
          style={{
              position: 'fixed',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              backgroundColor: '#52cd92',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              width: '50%'
          }}
      >
          <Toast.Header style={{ 
              backgroundColor: '#52cd92', 
              borderBottom: '1px solid rgba(255,255,255,0.2)',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              width: '100%'
          }}>
              <strong style={{ color: 'white' }}>{toastMessage}</strong>
          </Toast.Header>
      </Toast>
    </>
  );
};

export default CompleteMealCookingButton;
